import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Package, Truck, MapPin, Clock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { shipmentService } from '../services/shipmentService';

const TrackShipment = () => {
  const [waybillNumber, setWaybillNumber] = useState('');
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleTrack = async (e) => {
    e.preventDefault();
    
    if (!waybillNumber.trim()) {
      toast.error('Please enter a tracking number');
      return;
    }

    setLoading(true);
    try {
      const data = await shipmentService.trackShipment(waybillNumber);
      setTrackingData(data);
      
      if (!data || !data.ShipmentData || data.ShipmentData.length === 0) {
        toast.info('No tracking information available yet');
      }
    } catch (error) {
      console.error('Tracking error:', error);
      toast.error('Failed to track shipment. Please check your tracking number.');
      setTrackingData(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    const statusLower = status?.toLowerCase() || '';
    if (statusLower.includes('delivered')) return <CheckCircle className="text-green-500" />;
    if (statusLower.includes('transit') || statusLower.includes('out for delivery')) return <Truck className="text-blue-500" />;
    if (statusLower.includes('picked')) return <Package className="text-yellow-500" />;
    return <MapPin className="text-gray-500" />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-amber-50 py-16 px-4">
      <div className="container mx-auto max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-rose-900 mb-4">Track Your Shipment</h1>
          <p className="text-gray-600">Enter your tracking number to see real-time delivery status</p>
        </div>

        {/* Tracking Form */}
        <form onSubmit={handleTrack} className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex gap-4">
            <input
              type="text"
              placeholder="Enter Tracking Number / Waybill Number"
              value={waybillNumber}
              onChange={(e) => setWaybillNumber(e.target.value)}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent outline-none"
            />
            <Button
              type="submit"
              disabled={loading}
              className="bg-rose-700 hover:bg-rose-800 text-white px-8"
            >
              {loading ? 'Tracking...' : 'Track'}
            </Button>
          </div>
        </form>

        {/* Tracking Results */}
        {trackingData && trackingData.ShipmentData && trackingData.ShipmentData.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            {trackingData.ShipmentData.map((shipment, idx) => {
              const shipmentInfo = shipment.Shipment || {};
              const status = shipmentInfo.Status || {};
              const scans = shipmentInfo.Scans || [];

              return (
                <div key={idx}>
                  {/* Shipment Header */}
                  <div className="border-b pb-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-2xl font-bold text-gray-800">
                        Tracking: {shipment.Waybill || waybillNumber}
                      </h2>
                      <div className={`px-4 py-2 rounded-full font-semibold ${
                        status.Status?.toLowerCase().includes('delivered') 
                          ? 'bg-green-100 text-green-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {status.Status || 'In Transit'}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Origin</p>
                        <p className="font-semibold">{shipmentInfo.Origin || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Destination</p>
                        <p className="font-semibold">{shipmentInfo.Destination || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Expected Delivery</p>
                        <p className="font-semibold">
                          {shipmentInfo.ExpectedDeliveryDate || 'Being calculated'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Timeline */}
                  {scans && scans.length > 0 ? (
                    <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-6">Shipment Timeline</h3>
                      <div className="space-y-6">
                        {scans.map((scan, scanIdx) => (
                          <div key={scanIdx} className="flex gap-4">
                            <div className="flex flex-col items-center">
                              <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center">
                                {getStatusIcon(scan.ScanType)}
                              </div>
                              {scanIdx < scans.length - 1 && (
                                <div className="w-0.5 h-full bg-gray-200 mt-2"></div>
                              )}
                            </div>
                            
                            <div className="flex-1 pb-8">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold text-gray-800">
                                  {scan.ScanType || 'Status Update'}
                                </h4>
                                <span className="text-xs text-gray-500">
                                  {scan.ScanDateTime ? new Date(scan.ScanDateTime).toLocaleString() : ''}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 mb-1">
                                {scan.Instructions || 'No additional information'}
                              </p>
                              <p className="text-xs text-gray-500 flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {scan.ScannedLocation || 'Location not available'}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Package className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p>No tracking events available yet</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Help Section */}
        <div className="mt-8 text-center text-sm text-gray-600">
          <p>Need help? Contact our support at <a href="tel:+919876543210" className="text-rose-700 hover:underline">+91 98765 43210</a></p>
        </div>
      </div>
    </div>
  );
};

export default TrackShipment;
